/**
 * The two faces of Home, as rules.
 *
 * My Tasks used to be its own sidebar entry and its own URL. Folding it into
 * Home as a sub-tab is a navigation change, and navigation changes break two
 * things people never forgive: bookmarks, and links inside the app that were
 * written months ago. So the tabs are URL-addressable rather than local state,
 * and the old address still resolves.
 *
 * ── Why not useState ───────────────────────────────────────────────────────
 *
 * A local-state tab strip cannot be linked to, cannot be bookmarked, loses the
 * tab on refresh, and makes the browser Back button skip the whole section.
 * The CG Orders page does it that way and it is the thing people complain
 * about there. One path per tab costs nothing and fixes all four.
 *
 * My Notes briefly lived here as a third tab wrapping a Google Apps Script
 * iframe. It is now a native page with its own sidebar item at /notes, so
 * everything about that embed — the URL, the framing caveat, the escape hatch
 * — is gone rather than left behind commented out.
 */

export type HomeTabId = 'overview' | 'tasks' | 'shift';

export interface HomeTab {
  id: HomeTabId;
  label: string;
  /** The path this tab lives at. */
  path: string;
}

export const HOME_TABS: HomeTab[] = [
  { id: 'overview', label: 'Overview', path: '/home' },
  { id: 'tasks', label: 'My Tasks', path: '/home/tasks' },
  // Third, not second: the two things you open when you sit DOWN come first,
  // and this is the one you open when you stand up.
  { id: 'shift', label: 'My Shift', path: '/home/shift' },
];

export const DEFAULT_HOME_TAB: HomeTabId = 'overview';

/**
 * Which tab a path selects.
 *
 * Anything unrecognised falls back to Overview rather than rendering nothing:
 * a typo'd URL should show the dashboard, not an empty page that looks broken.
 */
export function tabFromPath(pathname: string): HomeTabId {
  const trimmed = pathname.replace(/\/+$/, '');
  const match = HOME_TABS.find((tab) => tab.path === trimmed);
  return match?.id ?? DEFAULT_HOME_TAB;
}

export function pathForTab(id: HomeTabId): string {
  return HOME_TABS.find((tab) => tab.id === id)?.path ?? '/home';
}

/**
 * Addresses that must keep working after My Tasks left the sidebar.
 *
 * `/` because it is the app root and half the team's browser opens there.
 * `/my-tasks` because it was a real page for months — it is in bookmarks, in
 * Slack messages, and in at least one link inside Home itself.
 */
export const HOME_REDIRECTS: { from: string; to: string }[] = [
  { from: '/', to: '/home' },
  { from: '/my-tasks', to: '/home/tasks' },
  // My Notes was a Home sub-tab for one day before becoming its own sidebar
  // item. Short-lived, but somebody had it open.
  { from: '/home/notes', to: '/notes' },
];
